import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FormLayout } from '../../../../models';
import { Row, Col, Button, Card, CardBody } from 'reactstrap';
import { logOut, loadApplicationForm, createApplication, startCreateApplication } from '../../../../redux';
import { destroy } from 'redux-form';
import { Loader, LoginForm, RegisterForm, FormRenderer } from '../../../../components';
import { normalizeFormLayoutSubmitValues } from '../../../../services/formLayoutGroupUtils';
import { applicationBelongsToUser, isApplicationFormReady } from './applicationFormUtils';
import { useEmbeddedResize } from '../../../../hooks/useEmbeddedResize';
import { getEmbedThemeFromUrl } from '../../../../hooks/embedTheme';

import './ApplicationForm.css';

const EMBED_THEME = getEmbedThemeFromUrl();

const ApplicationForm = props => {
    const dispatch              = useDispatch();
    const { t }                 = useTranslation();
    const { tkt_instance_id }   = props;
    const { user }              = useSelector(state => state.auth);
    const { applicationTypeId } = useParams();
    const containerRef = useRef(null);
    const themeClass = EMBED_THEME === 'dark' ? 'theme-dark' : 'theme-light';

    const {
        form,
        formResources,
        isLoading,
        hasLoadedForm,
        hasLoadingError: hasFormLoadingError
    } = useSelector(state => state.applicationTypes);

    const [ error, setError ]  = useState(undefined);

    const { application, isLoadingOne, loadingErrorMessage } = useSelector(state => state.applications);

    useEmbeddedResize(containerRef, [form, isLoading, isLoadingOne, user?.id, application]);

    useEffect(() => {
        if (applicationTypeId && tkt_instance_id) {
            dispatch(loadApplicationForm(
                applicationTypeId,
                tkt_instance_id
            ));
        }
    }, [applicationTypeId, dispatch, tkt_instance_id]);

    const logout = () => {
        dispatch(startCreateApplication());
        dispatch(destroy('formRenderer'));
        dispatch(logOut());
    }

    const layoutModel = React.useMemo(
        () => new FormLayout(form || { fields: [], groups: [] }),
        [form]
    );

    const formValues = useSelector((state) => state.form?.formRenderer?.values || {});
    const formReady = React.useMemo(
        () => isApplicationFormReady(layoutModel, formValues),
        [formValues, layoutModel]
    );

    const submit = (values) => {
        const fl = layoutModel;
        if (!isApplicationFormReady(fl, values)) {
            const availabilitiesField = fl.fields?.find(f => f.type === FormLayout.AVAILABILITIES);
            const sectorsField = fl.fields?.find(f => f.type === FormLayout.SECTORS);
            if (availabilitiesField && !values.availabilities?.length)
                return setError(t('applications.please_set_availabilities'));
            if (sectorsField && !values[sectorsField.id]?.length)
                return setError(t('applications.please_choose_sector'));
            return setError(t('forms.field_required'));
        }
        setError(undefined);

        const has_groups = (fl.groups || []).length > 0;
        const form_payload = has_groups
            ? normalizeFormLayoutSubmitValues(fl, values)
            : values;
        const availabilitiesField = fl.fields?.find(f => f.type === FormLayout.AVAILABILITIES);

        return dispatch(createApplication({
            ...form_payload,
            ...(availabilitiesField ? { availabilities: values.availabilities } : {}),
            type_id: applicationTypeId,
        }, {
            tkt_instance: tkt_instance_id
        }));
    }

    const postFields = React.useMemo(() => (
        error ? (
            <Col sm={12} md={12}>
                <div className="bg-danger text-white rounded my-2 p-2">
                    { error }
                </div>
            </Col>
        ) : null
    ), [error]);

    // let's check if the current user has already submitted
    // an application of this type
    const hasAlreadySubmitted = React.useMemo(() => {
        return user?.applications?.reduce((has, application) => has || application.type_id === applicationTypeId, false);
    }, [user, applicationTypeId]);

    if (isLoading || !hasLoadedForm)
        return (
            <div className={`ApplicationForm ${themeClass}`} ref={containerRef}>
                <div className="loader-wrapper center light">
                    <Loader color="#454545" />
                </div>
            </div>
        );

    if (!form)
        return (
            <div className={`ApplicationForm ${themeClass}`} ref={containerRef}>
                <div className="wrapper">
                    <div className={`container border rounded p-3 alert ${hasFormLoadingError ? 'alert-danger' : 'alert-warning'}`}>
                        <i className="fa fa-exclamation-triangle"></i>&nbsp;
                        {t(hasFormLoadingError
                            ? 'applications.mission_form_unavailable'
                            : 'applications.mission_form_missing'
                        )}
                    </div>
                </div>
            </div>
        );

    return (
        <div className={`ApplicationForm ${themeClass}`} ref={containerRef}>
            <div className="wrapper">
                <div className="container border rounded p-3 bg-white form-shell">
                    {!user?.id ? (
                    <>
                        <Row>
                            <Col sm={12} lg={6}>
                                <Card className="loginCard">
                                    <CardBody>
                                        <h3>{ t('login.title') }</h3>
                                        <LoginForm hide_logo />
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col sm={12} lg={6}>
                                <Card className="registerCard">
                                    <CardBody>
                                        <h3>{ t('register.title') }</h3>
                                        <RegisterForm tkt_instance_id={ tkt_instance_id } hide_logo />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                    ) : (
                        <>
                            <div className="d-flex flex-column items-end">
                                <h6>
                                    <i className="fa fa-user"></i>&nbsp; { user.fullname }
                                </h6>
                                <Button onClick={logout} size="xs" color="warning">
                                    <i className="fa fa-sign-out" />&nbsp;
                                    { t('applications.logout') }
                                </Button>
                            </div>
                            {loadingErrorMessage && (
                                <div className="alert alert-danger">
                                    <span dangerouslySetInnerHTML={{__html: loadingErrorMessage}}></span>
                                </div>
                            )}
                            {applicationBelongsToUser(application, user) ? (
                                <div className="success-message">
                                    { t('applications.successfully_submitted') }
                                </div>
                            ) : (hasAlreadySubmitted ? (
                                <div className="already-message">
                                    <i className="fa fa-check" />&nbsp;
                                    { t('applications.already_submitted') }
                                </div>
                            ) : (
                                <FormRenderer
                                    formLayout={ form }
                                    onSubmit={ submit }
                                    isLoading={ isLoadingOne }
                                    postFields={ postFields }
                                    submitLabel={ t('applications.submit_application') }
                                    submitIcon="paper-plane"
                                    submitDisabled={ !formReady }
                                    resources={ formResources }
                                />
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ApplicationForm;
